import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Settings, 
  Zap, 
  LayoutDashboard, 
  MessageCircle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  Trash2,
  ExternalLink,
  Loader2,
  Copy,
  Calendar,
  Lock,
  RefreshCw,
  AlertCircle,
  Instagram,
  User,
  Shield,
  Wifi,
  WifiOff,
  MessageSquare
} from 'lucide-react';

import DisconnectModal from '../ui/DisconnectModal';
import IntegrationInbox from '../ui/IntegrationInbox';
import ApiService from '../../services/ApiService';
import toast from 'react-hot-toast';

// ────────────────────────────────────────────────────────────────
// Helper: format ISO date strings to a readable local format
// ────────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  } catch (e) {
    return dateString;
  }
};

// ────────────────────────────────────────────────────────────────
// StatCard component
// ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color, bg }) => (
  <div className="bg-white border border-gray-200 p-4 sm:p-6 rounded-2xl flex items-center gap-3 sm:gap-5 transition-transform hover:scale-[1.02] shadow-sm">
    <div className={`p-2.5 sm:p-3.5 rounded-xl ${bg} border border-gray-100`}>
      <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${color}`} />
    </div>
    <div>
      <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
      <p className="text-xl sm:text-2xl font-black text-gray-900">{value}</p>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────
const IntegrationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(defaultTab);
  
  const [loading, setLoading] = useState(true);
  const [callbackProcessing, setCallbackProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [rawAccount, setRawAccount] = useState(null);   // raw object from API accounts[]
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [webhookToggles, setWebhookToggles] = useState({ comments: false, messages: false });
  const [webhookLoading, setWebhookLoading] = useState({ comments: false, messages: false });
  const [alreadyConnectedError, setAlreadyConnectedError] = useState(null); // { message }

  // const authUrl = "https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=704964296044481&redirect_uri=https://dev.uttik.com/integrations/instagram&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_content_publish%2Cinstagram_business_manage_insights";
  const authUrl = "https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=704964296044481&redirect_uri=https://app.uttik.com/integrations/instagram&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_content_publish%2Cinstagram_business_manage_insights";

  // ── Redirect away if no integration id parameter ────────────────
  useEffect(() => {
    if (!id) {
      navigate('/integrations');
    }
  }, [id, navigate]);

  // ── Fetch status ─────────────────────────────────────────────
  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getInstagramStatus();
      if (data?.success && data.accounts?.length > 0) {
        // Find the account by the integration_id from the URL
        const acc = data.accounts.find(a => a.integration_id === id) || data.accounts[0];
        setRawAccount(acc);
        const fields = acc?.webhook?.subscribed_fields || [];
        setWebhookToggles({
          comments: fields.includes('comments'),
          messages: fields.includes('messages'),
        });
      } else {
        setRawAccount(null);
      }
    } catch (err) {
      console.error('Error fetching Instagram status:', err);
      setError('Failed to load Instagram integration status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [id]);

  // ── Derived fields from nested API response ────────────────────
  // API shape:
  //  account.instagram_username, account.instagram_name,
  //  account.instagram_account_type, account.profile_picture_url
  //  token.is_token_valid, token.is_token_refreshable,
  //  token.scopes_granted, token.expires_at, token.connected_at
  //  webhook.is_receiving_events, webhook.subscribed_fields
  //  overall_status, integration_id
  const acct     = rawAccount?.account   || {};
  const token    = rawAccount?.token     || {};
  const webhook  = rawAccount?.webhook   || {};

  const instagramUsername    = acct.instagram_username    || '';
  const instagramName        = acct.instagram_name        || '';
  const instagramAccountType = acct.instagram_account_type || '';
  const profilePictureUrl    = acct.profile_picture_url   || '';

  const isTokenRefreshable  = token.is_token_refreshable  ?? false;
  const isTokenValid        = token.is_token_valid        ?? true;
  const scopesGranted       = token.scopes_granted        || [];
  const expiresAt           = token.expires_at            || null;
  const connectedAt         = token.connected_at          || null;

  const isReceivingEvents   = webhook.is_receiving_events ?? false;
  const subscribedFields    = webhook.subscribed_fields   || [];

  const overallStatus       = rawAccount?.overall_status  || 'unknown';
  const integrationId       = rawAccount?.integration_id  || '';

  const isConnected = !!rawAccount;
  const cannotManageWebhooks = overallStatus === 'token_expired' || overallStatus === 'disconnected';

  const TABS = [
    { id: 'overview',   label: 'Overview',     icon: LayoutDashboard },
    { id: 'inbox',      label: 'Inbox',        icon: MessageSquare },
    { id: 'settings',   label: 'Settings',     icon: Settings },
  ];

  // ── Webhook toggle handler ─────────────────────────────────
  const handleWebhookToggle = async (field) => {
    if (!integrationId || webhookLoading[field]) return;
    const currentlyOn = webhookToggles[field];
    const action = currentlyOn ? 'unsubscribe' : 'subscribe';
    setWebhookLoading((prev) => ({ ...prev, [field]: true }));
    try {
      const response = await ApiService.updateInstagramWebhook(
        integrationId,
        action,
        action === 'subscribe' ? [field] : []
      );
      if (response?.success) {
        setWebhookToggles((prev) => ({ ...prev, [field]: !currentlyOn }));
        toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} webhook ${action}d successfully!`);
      } else {
        throw new Error(response?.message || `Failed to ${action} webhook`);
      }
    } catch (err) {
      toast.error(err.message || `Failed to ${action} ${field} webhook`);
    } finally {
      setWebhookLoading((prev) => ({ ...prev, [field]: false }));
    }
  };

  // ── Disable both webhooks handler ──────────────────────────
  const handleDisableBothWebhooks = async () => {
    if (!integrationId || webhookLoading.comments || webhookLoading.messages) return;
    setWebhookLoading({ comments: true, messages: true });
    try {
      const response = await ApiService.updateInstagramWebhook(
        integrationId,
        'unsubscribe',
        []
      );
      if (response?.success) {
        setWebhookToggles({ comments: false, messages: false });
        toast.success(`Webhooks disabled successfully!`);
      } else {
        throw new Error(response?.message || `Failed to disable webhooks`);
      }
    } catch (err) {
      toast.error(err.message || `Failed to disable webhooks`);
    } finally {
      setWebhookLoading({ comments: false, messages: false });
    }
  };

  // ── Disconnect handler ─────────────────────────────────────────
  const handleDisconnect = async () => {
    if (!integrationId) return;
    
    try {
      setIsDisconnecting(true);
      const response = await ApiService.disconnectInstagram(integrationId);
      if (response?.success) {
        toast.success('Instagram integration disconnected successfully.');
        setRawAccount(null);
        setIsDisconnectModalOpen(false);
        navigate('/integrations');
      } else {
        throw new Error(response?.message || 'Failed to disconnect.');
      }
    } catch (err) {
      console.error('Error disconnecting Instagram:', err);
      toast.error(err.message || 'Failed to disconnect Instagram.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  // ── Refresh token handler ──────────────────────────────────────
  const handleRefreshToken = async () => {
    if (!isTokenRefreshable || !integrationId) return;

    try {
      const refreshToast = toast.loading('Refreshing Instagram token...');
      const response = await ApiService.refreshInstagramToken(integrationId);
      
      if (response?.success) {
        toast.success('Token refreshed successfully!', { id: refreshToast });
        if (response.accounts?.length > 0) {
          setRawAccount(response.accounts[0]);
        } else {
          fetchStatus();
        }
      } else {
        throw new Error(response?.message || 'Refresh failed.');
      }
    } catch (err) {
      console.error('Error refreshing token:', err);
      toast.error(err.message || 'Failed to refresh token.');
    }
  };

  // ══════════════════════════════════════════════════════════════
  // RENDER STATES
  // ══════════════════════════════════════════════════════════════

  // 1. OAuth callback in flight
  if (callbackProcessing) {
    return (
      <div className="min-h-[70vh] bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white border border-gray-200 rounded-3xl p-8 max-w-md w-full shadow-xl flex flex-col items-center text-center">
          <Loader2 className="h-14 w-14 text-black animate-spin mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">Connecting Instagram</h2>
          <p className="text-gray-500 mb-6 leading-relaxed">
            Exchanging authorization code and subscribing to webhooks. This will take a moment…
          </p>
          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <motion.div 
              className="bg-gradient-to-r from-purple-600 to-pink-500 h-1.5"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 10, ease: 'linear' }}
            />
          </div>
        </div>
      </div>
    );
  }

  // 2. Loading
  if (loading) {
    return (
      <div className="min-h-[70vh] bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-black animate-spin mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading integration details…</p>
        </div>
      </div>
    );
  }

  // 3. Error
  if (error) {
    return (
      <div className="min-h-[70vh] bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 rounded-3xl p-8 text-center max-w-md shadow-lg">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-4 border border-red-100 mx-auto">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Failed to load Integration</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button 
            onClick={fetchStatus}
            className="w-full py-3 bg-black text-white font-bold rounded-2xl hover:bg-gray-800 transition-all shadow-md flex items-center justify-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Retry Connection Check
          </button>
        </div>
      </div>
    );
  }

  // 4. Not connected — show Instagram login marketing page
  if (!isConnected) {
    return (
      <div className="integrations-root p-4 sm:p-8">
        <div className="max-w-2xl mx-auto mt-12 bg-white border border-gray-200 rounded-3xl p-8 sm:p-12 shadow-xl relative overflow-hidden">
          <div className="absolute -right-24 -top-24 w-60 h-60 bg-gradient-to-tr from-gray-700 via-gray-900 to-black opacity-10 rounded-full blur-3xl" />
          <div className="absolute -left-24 -bottom-24 w-60 h-60 bg-blue-500 opacity-5 rounded-full blur-3xl" />

          <div className="relative z-10 text-center flex flex-col items-center">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-tr from-gray-700 via-gray-900 to-black p-0.5 shadow-lg mb-6 flex items-center justify-center">
              <div className="w-full h-full bg-white rounded-[22px] flex items-center justify-center">
                <Instagram className="h-10 w-10 text-black stroke-[1.8px]" />
              </div>
            </div>

            <h1 className="text-3xl font-black text-gray-900 mb-4 tracking-tight leading-tight">
              Connect Instagram Business
            </h1>
            <p className="text-gray-500 max-w-lg mb-8 leading-relaxed font-medium">
              Automatically respond to comments, direct messages, and post interactions on your Instagram using Uttik&apos;s AI engine.
            </p>

            <div className="w-full max-w-md text-left space-y-4 mb-8 bg-gray-50 p-6 rounded-2xl border border-gray-100">
              {[
                { emoji: '💬', title: 'Auto-respond to DMs', desc: 'Answer inquiries instantly using data from your Knowledge Vault.' },
                { emoji: '⚡', title: 'Comment Automation', desc: 'Instantly reply to comments on your posts, boosting engagement.' },
                { emoji: '🔒', title: 'Official Meta Integration', desc: 'Fully secure, official OAuth API scopes ensuring compliance and security.' },
              ].map(({ emoji, title, desc }) => (
                <div key={title} className="flex gap-3">
                  <div className="h-6 w-6 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">{emoji}</div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-400">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => { window.location.href = authUrl; }}
              className="w-full sm:w-auto px-8 py-4 bg-black text-white font-extrabold rounded-2xl hover:bg-gray-800 transition-all flex items-center justify-center gap-3 shadow-lg shadow-black/10 text-sm"
            >
              <Instagram className="h-5 w-5" />
              Login with Instagram
            </button>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-4">🔒 Encrypted &amp; Meta Verified</p>
          </div>
        </div>

        {/* Error Modal for Already Connected Accounts */}
        <AnimatePresence>
          {alreadyConnectedError && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
              >
                <div className="p-8">
                  <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6">
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-3">Connection Failed</h3>
                  <p className="text-gray-600 font-medium leading-relaxed mb-8">
                    {alreadyConnectedError}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setAlreadyConnectedError(null)}
                      className="flex-1 py-3.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold rounded-xl transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

      </div>
    );
  }

  // 5. Connected — full details view
  return (
    <div className="integrations-root p-4 sm:p-6 xl:p-8 max-w-7xl mx-auto">

      {/* ── Header ───────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8 pb-8 border-b border-gray-100">
        <div className="flex items-center gap-5">
          <button 
            onClick={() => navigate('/integrations')}
            className="p-3 bg-white hover:bg-gray-50 text-gray-400 hover:text-black rounded-2xl border border-gray-200 transition-all shadow-sm group"
          >
            <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
          </button>

          <div className="flex items-center gap-4 sm:gap-5">
            {/* Profile picture or fallback icon */}
            <div className="w-16 h-16 rounded-[1.25rem] bg-white border border-gray-200 flex items-center justify-center overflow-hidden shadow-md flex-shrink-0">
              {profilePictureUrl ? (
                <img 
                  src={profilePictureUrl}
                  alt={instagramUsername}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML = '<div class="w-full h-full bg-gradient-to-tr from-gray-700 via-gray-900 to-black flex items-center justify-center"><svg xmlns=\'http://www.w3.org/2000/svg\' class=\'h-8 w-8 text-white\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'><path stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z\' /></svg></div>';
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-tr from-gray-700 via-gray-900 to-black flex items-center justify-center">
                  <Instagram className="h-8 w-8 text-white" />
                </div>
              )}
            </div>

            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-gray-900 leading-none mb-2 tracking-tight">
                {instagramName || 'Instagram Business'}
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <div className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 border ${
                  overallStatus === 'active' 
                    ? 'bg-green-50 text-green-700 border-green-200/50' 
                    : 'bg-red-50 text-red-700 border-red-200/50'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${overallStatus === 'active' ? 'bg-green-500' : 'bg-red-500'}`} />
                  {overallStatus === 'active' ? 'Active' : (overallStatus === 'token_expired' ? 'Token Expired' : overallStatus)}
                </div>
                {instagramUsername && (
                  <span className="text-sm text-gray-400 font-bold tracking-tight">@{instagramUsername}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {instagramUsername ? (
            <a 
              href={`https://www.instagram.com/${instagramUsername}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-3 bg-white hover:bg-gray-50 text-gray-900 text-xs font-black rounded-2xl border border-gray-200 transition-all shadow-sm"
            >
              <ExternalLink className="h-4 w-4 text-gray-400" />
              View Profile
            </a>
          ) : null}
          {overallStatus === 'token_expired' || overallStatus === 'disconnected' ? (
            <div className="flex gap-2">
                <button 
                  onClick={() => { window.location.href = authUrl; }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-black hover:bg-gray-800 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
                >
                  <Instagram className="h-4 w-4" />
                  Connect Again
                </button>
                {/* Delete button intentionally hidden when overall status is token_expired or disconnected */}
              </div>
          ) : (
            <button 
              onClick={() => setIsDisconnectModalOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-xl border border-red-100 transition-all shadow-sm"
            >
              <Trash2 className="h-4 w-4" />
              Disconnect
            </button>
          )}
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 bg-gray-100/50 p-1.5 rounded-[1.25rem] w-full sm:w-fit max-w-full mb-8 overflow-x-auto scrollbar-hide border border-gray-200/50">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2.5 px-8 py-3.5 rounded-[1rem] text-sm font-black transition-all whitespace-nowrap ${
              activeTab === tab.id 
              ? 'bg-white text-black shadow-lg shadow-black/5' 
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200/50'
            }`}
          >
            <tab.icon className={`h-4 w-4 ${activeTab === tab.id ? 'text-black' : ''}`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Webhook Warning Banner */}
      {webhookToggles && (!webhookToggles.messages || !webhookToggles.comments) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-bold text-amber-900">Webhook Warning</h4>
              <p className="text-xs text-amber-700 mt-1">
                {!webhookToggles.messages && !webhookToggles.comments 
                  ? "Auto message will not come, message webhook is disabled. Webhook is disabled for comment."
                  : !webhookToggles.messages 
                    ? "Auto message will not come, message webhook is disabled."
                    : "Webhook is disabled for comment."}
              </p>
            </div>
          </div>
          <button 
            onClick={() => setActiveTab('settings')}
            className="flex-shrink-0 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-bold rounded-xl transition-colors"
          >
            Change Setting
          </button>
        </div>
      )}


      {/* ── Tab Content ───────────────────────────────────────── */}
      <div className="min-h-[400px]">

        {/* ─── OVERVIEW ─────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >


            {/* Profile + Token info */}
            <div className="grid grid-cols-1 gap-4">

              {/* ── Left big card: Business Details ─────────── */}
              <div className="bg-white border border-gray-100 rounded-[1.5rem] overflow-hidden shadow-sm flex flex-col">

                {/* Card header */}
                <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-gray-50 bg-gray-50/30">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-gray-700 via-gray-900 to-black flex items-center justify-center flex-shrink-0">
                    <Instagram className="h-3.5 w-3.5 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Instagram Business Details</h3>
                </div>

                {/* Profile picture row */}
                <div className="flex items-center gap-4 px-5 sm:px-6 py-5 border-b border-gray-50">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gray-100 overflow-hidden flex-shrink-0 border border-gray-100 shadow-sm">
                    {profilePictureUrl ? (
                      <img
                        src={profilePictureUrl}
                        alt={instagramUsername}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://cdn.simpleicons.org/instagram/E4405F';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-tr from-gray-700 via-gray-900 to-black flex items-center justify-center">
                        <User className="h-9 w-9 text-white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-lg sm:text-xl font-black text-gray-900">{instagramName || '—'}</p>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                      {instagramUsername ? `@${instagramUsername}` : 'No username available'}
                    </p>
                    <span className="inline-block mt-2 px-2.5 py-1 bg-purple-50 border border-purple-100 text-purple-700 text-[10px] font-bold rounded-lg uppercase tracking-wider">
                      {instagramAccountType || 'Business'}
                    </span>
                  </div>
                </div>

                {/* Detail grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-gray-50 flex-1">
                  <div className="px-5 sm:px-6 py-5 space-y-5">
                    {/* Connected At */}
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 mb-1.5">
                        <Calendar className="h-3 w-3" />
                        Connected At
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {connectedAt ? formatDate(connectedAt) : 'N/A'}
                      </p>
                    </div>
                    {/* Overall Status */}
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Connection Status</p>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider border ${
                        overallStatus === 'active'
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : 'bg-gray-100 border-gray-200 text-gray-600'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${overallStatus === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                        {overallStatus}
                      </span>
                    </div>
                    {/* Token valid */}
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Token Valid</p>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider border ${
                        isTokenValid
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : 'bg-red-50 border-red-100 text-red-600'
                      }`}>
                        {isTokenValid ? '✓ Valid' : '✕ Expired'}
                      </span>
                    </div>
                  </div>
                  <div className="px-5 sm:px-6 py-5 space-y-5">
                    {/* Expires At */}
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 mb-1.5">
                        <Clock className="h-3 w-3" />
                        Expires At
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {expiresAt ? formatDate(expiresAt) : 'N/A'}
                      </p>
                    </div>
                    {/* Webhooks receiving */}
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Webhook Events</p>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider border ${
                        isReceivingEvents
                          ? 'bg-blue-50 border-blue-200 text-blue-700'
                          : 'bg-gray-100 border-gray-200 text-gray-500'
                      }`}>
                        {isReceivingEvents ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                        {isReceivingEvents ? 'Receiving' : 'Not Receiving'}
                      </span>
                    </div>
                    {/* Account type */}
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Account Type</p>
                      <span className="inline-block px-3 py-1 bg-purple-50 border border-purple-100 text-purple-700 text-xs font-bold rounded-lg uppercase tracking-wider">
                        {instagramAccountType || '—'}
                      </span>
                    </div>
                    {/* Subscribed Webhooks */}
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Subscribed Webhooks</p>
                      <div className="flex flex-wrap gap-2">
                        {subscribedFields.length > 0 ? (
                          subscribedFields.map((field) => (
                            <span key={field} className="px-2.5 py-1 bg-gray-100 border border-gray-200 text-gray-800 text-[10px] font-bold rounded-lg uppercase tracking-wide">
                              ● {field}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400 italic">No webhook subscriptions</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Token refresh row */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-5 sm:px-6 py-4 mt-auto border-t border-gray-50 bg-gray-50/30">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl border ${isTokenRefreshable ? 'bg-blue-50 border-blue-100' : 'bg-gray-100 border-gray-200'}`}>
                      <Lock className={`h-4 w-4 ${isTokenRefreshable ? 'text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">API Token Refresh</h4>
                      <p className="text-xs text-gray-400 font-medium">
                        {isTokenRefreshable
                          ? 'This token supports manual refresh'
                          : 'Token auto-renews — no manual refresh needed'}
                      </p>
                    </div>
                  </div>
                  <button
                    disabled={!isTokenRefreshable}
                    onClick={handleRefreshToken}
                    title={!isTokenRefreshable ? 'Token refresh is not available for this account' : 'Refresh your access token'}
                    className={`flex-shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 font-bold text-xs rounded-xl shadow-sm transition-all border ${
                      isTokenRefreshable
                        ? 'bg-black text-white hover:bg-gray-800 border-black cursor-pointer'
                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                    }`}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Refresh Token
                  </button>
                </div>
              </div>


            </div>
          </motion.div>
        )}



        {activeTab === 'inbox' && (
          <IntegrationInbox 
            integrationId={integrationId} 
            instagramAccounts={rawAccount ? [rawAccount] : []}
          />
        )}

        {activeTab === 'settings' && (
          <div className="bg-white border border-gray-200 rounded-3xl p-8 max-w-2xl shadow-sm">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Webhook Settings</h3>
            <p className="text-sm text-gray-400 font-medium mb-8">Control which Instagram events trigger real-time webhooks to Uttik.</p>
            <div className="space-y-4">

              {/* Webhook Toggles */}
              {webhookToggles.comments && webhookToggles.messages ? (
                <div className="flex items-center justify-between p-5 bg-gray-50 rounded-2xl border border-gray-100 transition-all hover:border-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border transition-colors bg-gray-100 border-gray-200">
                      <span className="text-base text-black">
                        ⚡
                      </span>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">Webhooks Enabled</h4>
                      <p className="text-xs text-gray-400 font-medium mt-0.5">Receiving both comments and messages events.</p>
                      <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-widest text-green-600">
                        ● Active
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleDisableBothWebhooks}
                    disabled={webhookLoading.comments || webhookLoading.messages || cannotManageWebhooks}
                    className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-all duration-300 focus:outline-none bg-black shadow-md shadow-black/20 ${(webhookLoading.comments || webhookLoading.messages || cannotManageWebhooks) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    title={cannotManageWebhooks ? 'Webhooks cannot be managed while token is expired or disconnected' : 'Disable all webhooks'}
                  >
                    <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-300 left-[26px]" />
                    {(webhookLoading.comments || webhookLoading.messages) && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <svg className="w-3.5 h-3.5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                      </span>
                    )}
                  </button>
                </div>
              ) : (
                [{ field: 'comments', label: 'Comments Webhook', desc: 'Receive events when users comment on your Instagram posts.' },
                  { field: 'messages', label: 'Messages Webhook', desc: 'Receive events when users send you Instagram direct messages.' }
                ].map(({ field, label, desc }) => (
                  <div key={field} className="flex items-center justify-between p-5 bg-gray-50 rounded-2xl border border-gray-100 transition-all hover:border-gray-200">
                    <div className="flex items-start gap-4">
                      <div className={`mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border transition-colors ${
                        webhookToggles[field] ? 'bg-gray-100 border-gray-200' : 'bg-gray-100 border-gray-200'
                      }`}>
                        <span className={`text-base ${webhookToggles[field] ? 'text-black' : 'text-gray-400'}`}>
                          {field === 'comments' ? '💬' : '📩'}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-gray-900">{label}</h4>
                        <p className="text-xs text-gray-400 font-medium mt-0.5">{desc}</p>
                        <span className={`inline-block mt-1.5 text-[10px] font-bold uppercase tracking-widest ${
                          webhookToggles[field] ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {webhookToggles[field] ? '● Active' : '○ Inactive'}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleWebhookToggle(field)}
                      disabled={webhookLoading[field] || cannotManageWebhooks}
                      className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-all duration-300 focus:outline-none ${
                        webhookToggles[field] ? 'bg-black shadow-md shadow-black/20' : 'bg-gray-200'
                      } ${(webhookLoading[field] || cannotManageWebhooks) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      title={
                        cannotManageWebhooks
                          ? 'Webhooks cannot be managed while token is expired or disconnected'
                          : `${webhookToggles[field] ? 'Unsubscribe from' : 'Subscribe to'} ${field} webhook`
                      }
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-300 ${
                        webhookToggles[field] ? 'left-[26px]' : 'left-0.5'
                      }`} />
                      {webhookLoading[field] && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <svg className="w-3.5 h-3.5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                        </span>
                      )}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <DisconnectModal 
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={handleDisconnect}
        integrationName="Instagram"
        isLoading={isDisconnecting}
      />

      {/* Error Modal for Connection / Authentication Errors */}
      <AnimatePresence>
        {alreadyConnectedError && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
            >
              <div className="p-8">
                <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6 border border-red-100">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-3">Connection Failed</h3>
                <p className="text-gray-600 font-medium leading-relaxed mb-8">
                  {alreadyConnectedError}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setAlreadyConnectedError(null)}
                    className="flex-1 py-3.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-900 font-bold rounded-xl transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IntegrationDetails;
